/**
 * 桥梁管理 - 桥梁信息表单控制逻辑
 */
import { createBridge, updateBridge, getBridgeById, getAllBridges } from '@/api/bridge';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import bridgeMapStore from '@/store/bridgeMapStore';
import { formatLatitude, formatLongitude } from '@/utils/coordinate';

export default {
  // 创建时的钩子函数，用于注册事件监听
  created() {
    // 监听状态重置事件
    BridgeEventBus.$on(BRIDGE_EVENTS.STATE_RESET, this.handleStateReset);
  },
  
  // 销毁前的钩子函数，用于移除事件监听
  beforeDestroy() {
    // 移除状态重置事件监听
    BridgeEventBus.$off(BRIDGE_EVENTS.STATE_RESET, this.handleStateReset);
  },
  computed: {
    // 从store获取位置拾取状态
    isLocationPickingActive() {
      return bridgeMapStore.state.isLocationPicking;
    }
  },
  
  data() {
    return {
      isEdit: false,
      // 表单数据
      bridgeForm: {
        id: null,
        bridgeName: '',
        bridgeCode: '',
        bridgeType: '',
        longitude: '',
        latitude: '',
        buildTime: '',
        structureInfo: '',
        clearanceHeight: '',
        warningHeight: '',
        warningSpeed: '',
        status: 1
      },
      // 表单验证规则
      rules: {
        bridgeName: [
          { required: true, message: '请输入桥梁名称', trigger: 'blur' },
          { max: 50, message: '长度不能超过50个字符', trigger: 'blur' }
        ],
        bridgeType: [
          { required: true, message: '请选择桥梁类型', trigger: 'change' }
        ],
        buildTime: [
          { required: true, message: '请选择建成时间', trigger: 'change' }
        ],
        clearanceHeight: [
          { required: true, message: '请输入净空高度', trigger: 'blur' },
          { type: 'number', message: '净空高度必须为数字', trigger: 'blur' }
        ],
        warningHeight: [
          { required: true, message: '请输入预警高度', trigger: 'blur' },
          { type: 'number', message: '预警高度必须为数字', trigger: 'blur' }
        ],
        warningSpeed: [
          { required: true, message: '请输入预警速度', trigger: 'blur' },
          { type: 'number', message: '预警速度必须为数字', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },
      // 桥梁类型选项
      bridgeTypeOptions: [
        { value: '斜拉桥', label: '斜拉桥' },
        { value: '悬索桥', label: '悬索桥' },
        { value: '拱桥', label: '拱桥' },
        { value: '梁桥', label: '梁桥' },
        { value: '钢架桥', label: '钢架桥' },
        { value: '其它', label: '其它' }
      ],
      // 桥梁标记
      bridgeMarkers: [],
      marker: null,
    };
  },

  methods: {
    // 处理状态重置事件
    handleStateReset() {
      console.log('表单控制器响应状态重置事件...');
      
      // 重置表单数据
      this.isEdit = false;
      
      // 重置桥梁表单
      this.bridgeForm = {
        id: null,
        bridgeName: '',
        bridgeCode: this.generateBridgeCode(), // 生成新编码
        bridgeType: '',
        longitude: '',
        latitude: '',
        buildTime: '',
        structureInfo: '',
        clearanceHeight: '',
        warningHeight: '',
        warningSpeed: '',
        status: 1
      };
      
      // 重置标记
      this.marker = null;
      
      // 重置桥梁标记列表
      this.bridgeMarkers = [];
      
      console.log('表单控制器状态已重置');
    },
    
    // 加载桥梁类型选项
    async loadBridgeTypeOptions() {
      try {
        console.log('开始加载桥梁类型选项...');
        // 尝试从数据字典加载桥梁类型选项
        const { getDictsByType } = await import('@/api/dict');
        const response = await getDictsByType('BRIDGE_TYPE');
        
        //console.log('桥梁类型API响应:', JSON.stringify(response));
        
        if (response && response.code === 200 && Array.isArray(response.data) && response.data.length > 0) {
          // 将API返回的数据转换为选项格式
          this.bridgeTypeOptions = response.data.map(item => ({
            value: item.dictValue,
            label: item.dictName || item.dictLabel || item.dictValue
          }));
          
          console.log('处理后的桥梁类型选项:', JSON.stringify(this.bridgeTypeOptions));
          
          // 如果没有设置当前桥梁类型且有选项可用，设置默认值
          if (!this.bridgeForm.bridgeType && this.bridgeTypeOptions.length > 0) {
            this.bridgeForm.bridgeType = this.bridgeTypeOptions[0].value;
          }
          
          console.log('桥梁类型加载成功，数量:', this.bridgeTypeOptions.length);
        } else {
          console.warn('加载桥梁类型失败或无数据，使用默认值');
        }
      } catch (error) {
        console.error('加载桥梁类型选项失败:', error);
      }
    },

    // 加载所有桥梁数据
    async loadAllBridges() {
      try {
        bridgeMapStore.actions.setLoading(true);
        const response = await getAllBridges();
        if (response && response.code === 200 && Array.isArray(response.data)) {
          // 同步到状态存储
          bridgeMapStore.actions.setBridges(response.data);
          this.bridgeMarkers = response.data;
        }
      } catch (error) {
        console.error('加载桥梁数据失败:', error);
        this.$message.error('加载桥梁数据失败');
      } finally {
        bridgeMapStore.actions.setLoading(false);
      }
    },
    
    // 加载桥梁标记
    loadBridgeMarkers(isEditMode = false) {
      try {
        if (isEditMode || this.bridgeMarkers.length === 0) {
          // 编辑模式或标记为空时才重新加载
          this.loadAllBridges();
        }
      } catch (error) {
        console.error('加载桥梁标记失败:', error);
      }
    },

    // 生成桥梁编号
    generateBridgeCode() {
      // 生成一个唯一的桥梁编码，格式为 BRIDGE_年月日_随机数
      const now = new Date();
      const dateStr = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
      const randomStr = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      this.bridgeForm.bridgeCode = `BRIDGE_${dateStr}_${randomStr}`;
      return this.bridgeForm.bridgeCode;
    },

    // 获取桥梁详情
    async fetchBridgeDetail(bridgeId) {
      if (!bridgeId) {
        console.warn('未提供桥梁ID');
        return Promise.reject(new Error('未提供桥梁ID'));
      }
      
      try {
        // 添加防御性代码：确保在异步操作前就设置loading状态
        this.loading = true;
        
        // 添加更详细的日志记录，帮助调试
        console.log('开始获取桥梁详情，ID:', bridgeId);
        
        // 尝试从缓存获取桥梁位置信息
        let cachedPosition = null;
        try {
          const positionCache = localStorage.getItem(`bridge_position_${bridgeId}`);
          if (positionCache) {
            cachedPosition = JSON.parse(positionCache);
            console.log(`已从缓存获取桥梁[${bridgeId}]位置信息`);
          }
        } catch (cacheError) {
          console.warn('读取桥梁位置缓存失败:', cacheError);
        }
        
        // 包装API调用，捕获所有可能的错误
        let response = null;
        try {
          response = await getBridgeById(bridgeId);
        } catch (apiError) {
          console.error('API调用失败:', apiError);
          throw new Error(`API请求失败: ${apiError.message || '未知错误'}`);
        }
        
        // 安全检查API响应
        if (!response) {
          throw new Error('API返回空响应');
        }
        
        if (response && response.code === 200 && response.data) {
          try {
            console.log('获取桥梁详情成功:', response.code);
            // 创建一个安全的桥梁数据对象
            const bridgeData = response.data;
            const safeData = {};
            
            // 逐个属性安全复制，避免直接传播null或undefined值
            if (bridgeData && typeof bridgeData === 'object') {
              Object.keys(bridgeData).forEach(key => {
                // 确保不会有undefined或null值
                if (bridgeData[key] !== undefined && bridgeData[key] !== null) {
                  // 对字符串类型的值进行特殊处理，防止后续方法调用出错
                  if (typeof bridgeData[key] === 'string') {
                    safeData[key] = bridgeData[key];
                  } else {
                    safeData[key] = bridgeData[key];
                  }
                }
              });
            } else {
              console.warn('API返回的数据不是对象类型:', typeof bridgeData);
            }
            
            // 合并到当前表单
            this.bridgeForm = { 
              ...this.bridgeForm,
              ...safeData
            };
            
            // 缓存桥梁位置信息到localStorage
            if (this.bridgeForm.id && this.bridgeForm.latitude && this.bridgeForm.longitude) {
              bridgeMapStore.actions.cacheBridgePosition(this.bridgeForm);
            }
            
            // 安全检查：如果有经纬度数据，设置marker
            if (this.bridgeForm.latitude && 
                this.bridgeForm.longitude && 
                !isNaN(this.bridgeForm.latitude) && 
                !isNaN(this.bridgeForm.longitude)) {
              this.marker = [
                parseFloat(this.bridgeForm.latitude), 
                parseFloat(this.bridgeForm.longitude)
              ];
              
              // 如果地图实例已经存在，设置视图中心点
              if (this.map) {
                console.log('设置地图中心到桥梁位置:', this.marker);
                this.map.setView(this.marker, 15);
                
                // 确保地图刷新
                setTimeout(() => {
                  try {
                    this.map.invalidateSize();
                  } catch (err) {
                    console.warn('地图刷新失败，但不影响使用');
                  }
                }, 200);
              }
            }
            
            // 同步到状态存储前进行安全处理
            try {
              bridgeMapStore.actions.setCurrentBridge(this.bridgeForm);
            } catch (storeError) {
              console.warn('同步到状态存储失败，但不影响正常功能:', storeError);
            }
            
            // 发送桥梁数据加载完成事件 - 也添加防御性代码
            try {
              // 确保事件常量可用且有效
              if (BRIDGE_EVENTS && BRIDGE_EVENTS.BRIDGE_DATA_LOADED) {
                console.log('发送桥梁加载完成事件:', BRIDGE_EVENTS.BRIDGE_DATA_LOADED);
                BridgeEventBus.$emit(BRIDGE_EVENTS.BRIDGE_DATA_LOADED, this.bridgeForm);
              } else {
                console.warn('无法发送桥梁加载完成事件: 事件常量未定义');
              }
            } catch (eventError) {
              console.warn('发送桥梁数据加载事件失败，但不影响功能:', eventError);
            }

            // 返回加载的桥梁数据
            return this.bridgeForm;
          } catch (dataProcessError) {
            console.error('处理API返回的桥梁数据时出错:', dataProcessError);
            throw new Error(`数据处理错误: ${dataProcessError.message || '未知错误'}`);
          }
        } else {
          const errMsg = response?.message || '未知错误';
          console.error('加载桥梁信息失败:', errMsg, '返回代码:', response?.code);
          this.$message.error('加载桥梁信息失败');
          throw new Error(`加载桥梁信息失败: ${errMsg}`);
        }
      } catch (error) {
        console.error('获取桥梁详情失败:', error);
        this.$message.error(`获取桥梁详情失败: ${error.message || '未知错误'}`);
        this.loading = false;
        return Promise.reject(error);
      } finally {
        this.loading = false;
      }
    },
    
    // 切换位置拾取模式 - 使用store管理
    toggleLocationPicking() {
      // 调用store中的方法，不需要在本地保存状态
      bridgeMapStore.actions.toggleLocationPicking();
    },

    // 准备表单数据，用于API提交
    prepareFormData(data) {
      // 创建数据副本，避免直接修改原始数据
      const formData = { ...data };
      
      // 确保数字字段为数字类型
      ['clearanceHeight', 'warningHeight', 'warningSpeed'].forEach(field => {
        if (formData[field] !== undefined && formData[field] !== null && formData[field] !== '') {
          formData[field] = parseFloat(formData[field]);
        }
      });
      
      // 确保坐标格式正确
      if (formData.latitude) formData.latitude = parseFloat(formData.latitude);
      if (formData.longitude) formData.longitude = parseFloat(formData.longitude);
      
      return formData;
    },

    // 提交桥梁表单
    async submitBridgeForm() {
      if (!this.$refs.bridgeForm) return false;
      
      try {
        // 表单验证
        const valid = await this.$refs.bridgeForm.validate();
        
        if (!valid) {
          this.$message.warning('请完善表单信息');
          return false;
        }
        
        // 准备提交的数据
        const formData = this.prepareFormData(this.bridgeForm);
        
        this.submitting = true;
        
        // 根据编辑状态选择创建或更新API
        const promise = this.isEdit ? 
          updateBridge(formData.id, formData) : 
          createBridge(formData);
        
        const response = await promise;
        
        if (response && response.code === 200) {
          this.$message.success(this.isEdit ? '更新桥梁成功' : '创建桥梁成功');
          
          // 更新本地表单数据，确保ID存在
          if (!this.isEdit && response.data && response.data.id) {
            this.bridgeForm.id = response.data.id;
            this.isEdit = true;
          }
          
          // 将成功保存的桥梁数据同步到状态存储
          bridgeMapStore.actions.setCurrentBridge(this.bridgeForm);
          
          // 更新桥梁位置缓存
          if (this.bridgeForm.id && this.bridgeForm.latitude && this.bridgeForm.longitude) {
            bridgeMapStore.actions.cacheBridgePosition(this.bridgeForm);
          }
          
          // 发送桥梁保存成功的事件
          try {
            const eventName = this.isEdit ? BRIDGE_EVENTS.BRIDGE_UPDATED : BRIDGE_EVENTS.BRIDGE_CREATED;
            
            // 确保事件名有效
            if (eventName) {
              console.log('发送桥梁保存成功事件:', eventName);
              BridgeEventBus.$emit(eventName, this.bridgeForm);
            } else {
              console.warn('无法发送桥梁保存成功事件: 事件常量未定义');
            }
          } catch (eventError) {
            console.warn('发送桥梁保存成功事件失败，但不影响功能:', eventError);
          }
          
          return true;
        } else {
          this.$message.error(response?.message || '保存失败');
          return false;
        }
      } catch (error) {
        console.error('保存失败:', error);
        this.$message.error('保存失败，请检查网络连接');
        return false;
      } finally {
        this.submitting = false;
      }
    },

    // 重置表单
    resetForm() {
      // 重置表单数据
      if (this.$refs.bridgeForm) {
        this.$refs.bridgeForm.resetFields();
      }
      
      // 重置为新桥梁状态
      this.isEdit = false;
      this.bridgeForm = {
        id: null,
        bridgeName: '',
        bridgeCode: this.generateBridgeCode(),
        bridgeType: '',
        longitude: '',
        latitude: '',
        buildTime: '',
        structureInfo: '',
        clearanceHeight: '',
        warningHeight: '',
        warningSpeed: '',
        status: 1
      };
      
      // 清除标记
      this.marker = null;
    },

    // 显示桥梁信息
    showBridgeInfo(bridge) {
      if (bridge && bridge.id) {
        this.fetchBridgeDetail(bridge.id);
      }
    },

    // 编辑桥梁
    editBridge(bridge) {
      if (bridge && bridge.id) {
        this.fetchBridgeDetail(bridge.id);
      }
    },
    
    // 返回桥梁列表
    goBack() {
      this.$router.push('/bridge/list');
    },
    
    // 格式化坐标显示
    formatCoordinate(value, type) {
      if (!value || isNaN(value)) return '';
      
      if (type === 'longitude') {
        return formatLongitude(value);
      } else if (type === 'latitude') {
        return formatLatitude(value);
      }
      
      return value;
    }
  }
}; 